/** Name the utterances of ONE detected voice (#2371).
 *
 * A voice identity decision is keyed on the original diarization label, so the
 * name it puts on screen has to follow the same key. Renaming by display name
 * instead — "rename everyone currently called X", which is the right rule for
 * a manual rename — is what made naming a second speaker erase the first:
 *
 *   1. both detected voices start out showing the same source label as their
 *      display name, or are both unnamed;
 *   2. naming the first voice renames every utterance that shares that name,
 *      the second voice's utterances included;
 *   3. naming the second voice then renames every utterance showing the first
 *      person's name — so the first person disappears from the transcript.
 *
 * Positions come from the backend's own timeline, which is the same thing the
 * decision is stored against, so the utterances renamed here are exactly the
 * ones that decision covers.
 *
 * Lives on its own, away from the editor contexts, because the transcript
 * editor and the translation editor both need it and because the two-speaker
 * case above is worth pinning down in a test of its own. */

import { VoiceTimelineSpan } from '@utils/types/organization/voice-profile.ts';
import { rowBelongsToLabel } from '@utils/editors/voice-sample-ranges.ts';

interface TimedSpeakerRow {
  speaker?: string;
  start?: number;
  stop?: number;
}

/** The utterances with this voice's rows renamed, or null when nothing
 *  changes — so a caller can skip the undo entry and the dirty flag. */
export const applyVoiceNameToUtterances = <T extends TimedSpeakerRow>(
  utterances: T[],
  timeline: VoiceTimelineSpan[],
  speakerLabel: string,
  newSpeaker: string
): T[] | null => {
  if (!newSpeaker || !timeline.length || !utterances.length) return null;

  let changed = false;
  const updated = utterances.map(utterance => {
    if (utterance.speaker === newSpeaker) return utterance;
    if (
      !rowBelongsToLabel(
        timeline,
        speakerLabel,
        utterance.start,
        utterance.stop
      )
    )
      return utterance;
    changed = true;
    return { ...utterance, speaker: newSpeaker };
  });

  return changed ? updated : null;
};
